import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useBillPackageStore } from '@/stores/bill-package'
import {
  Alert,
  Fade,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Modal } from '../../../components'
import { useInputText } from '../../../hooks'
import services from '../../../services'
import { ModalUpdateProps } from './@types'
import { EXPIRATION_OPTIONS } from './expiration-options'

const PACKAGES_QUERY_KEY = ['/bill-packages']

export default function ModalUpdate({ open, onClose }: ModalUpdateProps) {
  const queryClient = useQueryClient()
  const { billPackage, clearPackage } = useBillPackageStore()
  const initialValues = {
    name: '',
    price: '',
    description: '',
    resourcesText: '',
    expirationDays: '',
  }
  const input = useInputText(initialValues)

  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const closeModal = () => {
    input.clear()
    setError(null)
    clearPackage()
    onClose?.()
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    try {
      event.preventDefault()
      if (!billPackage) return
      if (error) setError(null)
      setLoading(true)

      const { name, price, description, resourcesText, expirationDays } = input
      const resources = resourcesText
        ? resourcesText
            .split(/[,;\n]/)
            .map((s) => s.trim())
            .filter(Boolean)
        : undefined
      const expirationDaysNum =
        expirationDays !== '' ? parseInt(expirationDays, 10) : null

      await services.packages.update({
        id: billPackage.id,
        name: name || undefined,
        price: price || undefined,
        description: description || undefined,
        resources,
        ...(expirationDaysNum !== undefined && { expirationDays: expirationDaysNum }),
      })

      queryClient.invalidateQueries({ queryKey: PACKAGES_QUERY_KEY })
      toast.success('Pacote atualizado com sucesso.')
      closeModal()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (billPackage && open) {
      input.updateInitial('name', billPackage.name)
      input.updateInitial('price', String(billPackage.price))
      input.updateInitial('description', billPackage.description ?? '')
      input.updateInitial(
        'resourcesText',
        Array.isArray(billPackage.resources)
          ? billPackage.resources.join(', ')
          : '',
      )
      const expirationValue =
        billPackage.expirationDays != null &&
        EXPIRATION_OPTIONS.some(
          (o) => o.value !== '' && Number(o.value) === billPackage.expirationDays,
        )
          ? String(billPackage.expirationDays)
          : ''
      input.updateInitial('expirationDays', expirationValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billPackage?.id, open])

  return (
    <Modal
      open={open}
      onClose={closeModal}
      head={<h2 className="text-xl font-bold">Atualizar pacote</h2>}
    >
      <Fade in={open}>
        <div className="text-xl grid gap-12">
          {loading && <Alert severity="warning">A enviar dados...</Alert>}
          {error && <Alert severity="error">{error}</Alert>}

          <form onSubmit={handleSubmit}>
            <ScrollArea className="h-[380px] w-full">
              <div className="grid gap-8 my-2">
                <TextField
                  autoFocus
                  required
                  label="Nome"
                  name="name"
                  value={input.name}
                  onChange={input.onChange}
                  onBlur={input.onBlur}
                  error={!!input.errors.name}
                  helperText={input.errors.name}
                  disabled={loading}
                />

                <TextField
                  required
                  label="Preço (MZN)"
                  name="price"
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  value={input.price}
                  onChange={input.onChange}
                  onBlur={input.onBlur}
                  error={!!input.errors.price}
                  helperText={input.errors.price}
                  disabled={loading}
                />

                <TextField
                  required
                  label="Descrição"
                  name="description"
                  multiline
                  minRows={2}
                  value={input.description}
                  onChange={input.onChange}
                  onBlur={input.onBlur}
                  error={!!input.errors.description}
                  helperText={input.errors.description}
                  disabled={loading}
                />

                <TextField
                  label="Recursos (separados por vírgula ou nova linha)"
                  name="resourcesText"
                  multiline
                  minRows={2}
                  placeholder="Ex: Acesso a vídeos, Suporte por email"
                  value={input.resourcesText}
                  onChange={input.onChange}
                  disabled={loading}
                />

                <FormControl fullWidth disabled={loading}>
                  <InputLabel id="expiration-days-label-update">
                    Validade
                  </InputLabel>
                  <Select
                    labelId="expiration-days-label-update"
                    label="Validade"
                    name="expirationDays"
                    value={input.expirationDays}
                    onChange={(e: SelectChangeEvent) =>
                      input.updateInitial('expirationDays', e.target.value)
                    }
                  >
                    {EXPIRATION_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value || 'default'} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </ScrollArea>

            <div className="w-max max-w-full ml-auto mt-2 gap-4 flex">
              <Button
                disabled={loading}
                variant="outline"
                type="button"
                onClick={closeModal}
              >
                Cancelar
              </Button>
              <Button disabled={loading} type="submit">
                Atualizar
              </Button>
            </div>
          </form>
        </div>
      </Fade>
    </Modal>
  )
}
